import { useCallback, useEffect, useState } from 'react';
import { Building2, Loader2, Trash2, User, X } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

interface Share {
  id: string;
  granteeType: 'user' | 'department';
  granteeId: string;
  label: string;
  permission: 'view' | 'edit';
}

interface Targets {
  users: { id: string; email: string }[];
  departments: { id: string; name: string }[];
}

async function errorOf(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? `HTTP ${res.status}`;
}

/** Share one lead with people or departments (read-only or editable). Only shown to the lead's owner and admins. */
export default function LeadShareDialog({ leadId, leadName, onClose }: { leadId: string; leadName: string; onClose: () => void }) {
  const { t } = useAppTranslation();
  const [shares, setShares] = useState<Share[] | null>(null);
  const [targets, setTargets] = useState<Targets>({ users: [], departments: [] });
  const [recipient, setRecipient] = useState('');
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [s, tg] = await Promise.all([
      fetch(`/api/leads/${leadId}/shares`, { credentials: 'include' }),
      fetch('/api/leads/share-targets', { credentials: 'include' }),
    ]);
    if (!s.ok) { setError(await errorOf(s)); return; }
    setShares((await s.json()) as Share[]);
    if (tg.ok) setTargets((await tg.json()) as Targets);
  }, [leadId]);

  useEffect(() => { void load(); }, [load]);

  async function add() {
    if (!recipient) return;
    const [granteeType, granteeId] = recipient.split(':') as ['user' | 'department', string];
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/leads/${leadId}/shares`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ granteeType, granteeId, permission }),
      });
      if (!res.ok) { setError(await errorOf(res)); return; }
      setRecipient('');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function remove(share: Share) {
    setError('');
    const res = await fetch(`/api/leads/${leadId}/shares/${share.id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok && res.status !== 204) { setError(await errorOf(res)); return; }
    await load();
  }

  const taken = new Set((shares ?? []).map((s) => `${s.granteeType}:${s.granteeId}`));
  const users = targets.users.filter((u) => !taken.has(`user:${u.id}`));
  const departments = targets.departments.filter((d) => !taken.has(`department:${d.id}`));
  const permLabel = (p: Share['permission']) => (p === 'edit' ? t('leads.share.edit', 'Bearbeiten') : t('leads.share.view', 'Nur lesen'));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('leads.share.title', 'Lead teilen')}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl bg-panel border border-line p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-ink">{t('leads.share.title', 'Lead teilen')}</h3>
          <button type="button" onClick={onClose} aria-label={t('common.close', 'Schließen')} className="rounded-lg p-1.5 text-ink-muted hover:bg-raised">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-5 text-sm text-ink-muted">{t('leads.share.intro', '„{{name}}“ ist standardmäßig nur für dich und Admins sichtbar. Gib ihn gezielt frei.', { name: leadName })}</p>

        <div className="rounded-xl border border-line p-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted" htmlFor="share-recipient">
            {t('leads.share.recipient', 'Person oder Abteilung')}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              id="share-recipient"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent-500/50 focus:outline-none focus:ring-1 focus:ring-accent-500/30"
            >
              <option value="">{t('leads.share.choose', 'Auswählen …')}</option>
              {departments.length > 0 && (
                <optgroup label={t('leads.share.departments', 'Abteilungen')}>
                  {departments.map((d) => <option key={d.id} value={`department:${d.id}`}>{d.name}</option>)}
                </optgroup>
              )}
              {users.length > 0 && (
                <optgroup label={t('leads.share.people', 'Personen')}>
                  {users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.email}</option>)}
                </optgroup>
              )}
            </select>
            <select
              aria-label={t('leads.share.permission', 'Berechtigung')}
              value={permission}
              onChange={(e) => setPermission(e.target.value as 'view' | 'edit')}
              className="rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent-500/50 focus:outline-none focus:ring-1 focus:ring-accent-500/30"
            >
              <option value="view">{permLabel('view')}</option>
              <option value="edit">{permLabel('edit')}</option>
            </select>
            <button
              type="button"
              disabled={!recipient || busy}
              onClick={() => void add()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:bg-accent-500 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('leads.share.add', 'Freigeben')}
            </button>
          </div>
          {users.length === 0 && departments.length === 0 && shares !== null && (
            <p className="mt-2 text-xs text-ink-faint">{t('leads.share.none', 'Keine weiteren Personen oder Abteilungen verfügbar.')}</p>
          )}
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-danger-600 dark:text-danger-300">{error}</p>}

        <h4 className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{t('leads.share.current', 'Geteilt mit')}</h4>
        {shares === null && !error && <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />}
        {shares !== null && shares.length === 0 && <p className="text-sm text-ink-faint">{t('leads.share.private', 'Noch niemand. Der Lead ist privat.')}</p>}
        <ul className="space-y-2">
          {(shares ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-3 rounded-lg border border-line px-3 py-2">
              {s.granteeType === 'department' ? <Building2 className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" /> : <User className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />}
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{s.label}</span>
              <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted">{permLabel(s.permission)}</span>
              <button
                type="button"
                onClick={() => void remove(s)}
                title={t('leads.share.revoke', 'Freigabe entziehen')}
                aria-label={t('leads.share.revoke', 'Freigabe entziehen')}
                className="rounded-lg p-1.5 text-ink-muted hover:bg-danger-500/10 hover:text-danger-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
